import { useState, useCallback } from 'react';
import { auth } from '@/lib/firebase';
import type { Job, JobSummary, SearchCriteria, ReviewStatus } from '@/types/job';

interface UseJobsReturn {
  jobs: JobSummary[];
  loading: boolean;
  error: string | null;
  listJobs: () => Promise<void>;
  createJob: (params: { sourceVideoPath: string; searchCriteria: SearchCriteria }) => Promise<{ jobId: string }>;
  getJob: (jobId: string) => Promise<Job>;
  deleteJob: (jobId: string) => Promise<void>;
  deleteVideo: (filePath: string) => Promise<void>;
  reviewClip: (jobId: string, clipIndex: number, status: ReviewStatus) => Promise<void>;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export function useJobs(): UseJobsReturn {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/jobs', { headers });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ?? 'Failed to fetch jobs');
      }
      const data = await response.json();
      setJobs(data.jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const createJob = useCallback(
    async (params: { sourceVideoPath: string; searchCriteria: SearchCriteria }): Promise<{ jobId: string }> => {
      setLoading(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const response = await fetch('/api/jobs', {
          method: 'POST',
          headers,
          body: JSON.stringify(params),
        });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error ?? 'Failed to create job');
        }
        const data = await response.json();
        return { jobId: data.jobId };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getJob = useCallback(async (jobId: string): Promise<Job> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/jobs/${jobId}`, { headers });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error ?? 'Failed to fetch job');
    }
    return response.json();
  }, []);

  const deleteJob = useCallback(async (jobId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/jobs/${jobId}`, { method: 'DELETE', headers });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error ?? 'Failed to delete job');
    }
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const deleteVideo = useCallback(async (filePath: string) => {
    const headers = await getAuthHeaders();
    const encoded = encodeURIComponent(filePath);
    const response = await fetch(`/api/videos/${encoded}`, { method: 'DELETE', headers });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error ?? 'Failed to delete video');
    }
  }, []);

  const reviewClip = useCallback(async (jobId: string, clipIndex: number, status: ReviewStatus) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/jobs/${jobId}/clips/${clipIndex}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ reviewStatus: status }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error ?? 'Failed to update clip');
    }
  }, []);

  return { jobs, loading, error, listJobs, createJob, getJob, deleteJob, deleteVideo, reviewClip };
}
